import { invoke } from "@tauri-apps/api/core";
import type { Toolkit } from "@/api/integrations";
import { isTauri } from "@/lib/is-tauri";
import { useIntegrationStore } from "@/stores/integration-store";
import type { WorkToastAppRef } from "@/stores/work-toast-store";

export interface AppVisual {
  appId: string;
  label: string;
  iconUrl: string | null;
}

interface NativeAppPreview {
  appName: string;
  bundleId?: string | null;
  iconDataUrl: string;
}

const cache = new Map<string, Promise<AppVisual | null>>();
let currentApp: Promise<AppVisual | null> | undefined;

/** The running app's packaged icon, read by the same native icon renderer. */
export function resolveCurrentAppVisual(): Promise<AppVisual | null> {
  if (!isTauri()) return Promise.resolve(null);
  currentApp ??= invoke<NativeAppPreview>("get_current_app_preview")
    .then((preview) =>
      preview?.iconDataUrl
        ? {
            appId: resolvedAppId(preview),
            label: preview.appName,
            iconUrl: preview.iconDataUrl,
          }
        : null,
    )
    .catch(() => null);
  return currentApp;
}

export function resolvedAppId(
  preview: Pick<NativeAppPreview, "appName" | "bundleId">,
): string {
  return (preview.bundleId ?? preview.appName).toLowerCase();
}

async function lookupDesktop(
  appHint: string,
  executionId?: string,
): Promise<AppVisual | null> {
  if (!isTauri()) return null;
  const args = executionId ? { appHint, executionId } : { appHint };
  const preview = await invoke<NativeAppPreview>(
    "get_native_app_preview",
    args,
  ).catch(() => null);
  if (!preview?.iconDataUrl) return null;
  return {
    appId: resolvedAppId(preview),
    label: preview.appName,
    iconUrl: preview.iconDataUrl,
  };
}

function toolkitVisual(
  slug: string,
  toolkit: Toolkit | undefined,
): AppVisual | null {
  if (!toolkit?.icon_url) return null;
  return {
    appId: slug,
    label: toolkit.display_name || slug,
    iconUrl: toolkit.icon_url,
  };
}

async function lookupToolkit(slug: string): Promise<AppVisual | null> {
  return toolkitVisual(
    slug,
    await useIntegrationStore.getState().actions.resolveToolkit(slug),
  );
}

/** The app the execution's latest desktop step worked in, as the companion
 *  recorded it from the step's result; the frontmost app only when that step
 *  named none. Resolved on each call because both change. */
export function resolveOperatedAppVisual(
  executionId: string,
): Promise<AppVisual | null> {
  return lookupDesktop("frontmost", executionId).then((visual) => {
    if (visual) cache.set(visual.appId, Promise.resolve(visual));
    return visual;
  });
}

/** Cache one lookup promise per app for this window, including misses. */
export function resolveAppVisual(
  appId: string,
  route: WorkToastAppRef["route"],
): Promise<AppVisual | null> {
  const key = appId.trim().toLowerCase();
  if (!key || route === "provider") return Promise.resolve(null);
  const cached = cache.get(key);
  if (cached) return cached;
  const pending = route === "mcp" ? lookupToolkit(key) : lookupDesktop(key);
  cache.set(key, pending);
  return pending;
}

export function resetWorkToastAppVisualsForTests(): void {
  cache.clear();
  currentApp = undefined;
}
